import React, { Suspense, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import tw from 'twin.macro';
import { Loading } from '../loading';
import { TextLine, Title, TitleRow } from '../text';
import { AppContext, PreferenceContext, UserContext } from '../context';
import { Ads, LazyloadAds, SideBarAds } from '../ads';
import {
  ItemActionsBar,
  ItemContext,
  ItemDetailsMedia,
  ItemMobileSelector,
  ItemSummary,
  ItemTags,
} from './';
import { MetaTags } from '../meta';
import { ItemsGrid, ItemsList } from '../items';
import { DropDown, DropDownItem } from '../dropdown';
import { Container } from '../container';
import { gaEvent } from 'services/ga';
import { useIncreaseItemViews } from '../common/hooks';
import { SideBarComments } from '../sidebars';
import { DateInput, isWrongDay, isWrongMonth, isWrongYear } from '../form/DateInput';
import { Button } from '../button';
import { userChangeBirthDate, userUpdateAccessToken } from 'services/user';
import { notifyError, notifySuccess } from '../notifications';
import { convertToDate } from '../../lib/helpers';

const RedirectPage = ({ itemId, currentItem }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { slideIn, setSlideIn } = useContext(AppContext);
  const { id, secret, title, description, stills, tags, nsfw } = currentItem;
  const itemMeta = { id, secret, title, description, stills, media: [], tags, nsfw };

  // TODO (MINOR): needs to support setting status code and send redirect directly on server side request
  useEffect(() => {
    navigate(`/inloggen?next=/item/${itemId}${location.hash}`, {
      replace: true,
      state: { nsfwHeader: true, currentItem: itemMeta },
    });
    setSlideIn({ ...slideIn, active: false, Component: () => <></> });
  }, []);

  return null;
};

const selectedListOptions = [
  { label: 'Nieuw', value: 'latest' },
  { label: 'Toppers', value: 'toppers' },
  { label: 'Gerelateerd', value: 'related' },
];

const SelectedList = () => {
  const {
    preference: {
      listType,
      listType: { detailsPage: detailsPageList },
    },
    setPreference,
  } = useContext(PreferenceContext);

  return (
    <DropDown
      label={`${
        detailsPageList && selectedListOptions.find((option) => option.value === detailsPageList)
          ? `${selectedListOptions.find((option) => option.value === detailsPageList).label}`
          : 'selecteer een lijst'
      }`}
      isSelect
      isRight
      buttonProps={{ secondary: true }}
    >
      {selectedListOptions.map((option) => (
        <DropDownItem
          label={option.label}
          key={option.label}
          selectedValue={detailsPageList === option.value}
          onClick={() => {
            gaEvent('dmp_detail_page', 'item_list_select_' + option.value);
            setPreference({ listType: { ...listType, detailsPage: option.value } });
          }}
        />
      ))}
    </DropDown>
  );
};

const RightBlock = () => <SelectedList menuRight />;

const TooYoungBlock = () => (
  <div tw='sm:aspect-w-16 sm:aspect-h-9 p-2 py-6 border-8 border-red-500'>
    <div tw='flex flex-col justify-center items-center'>
      <div tw='text-center mb-6'>
        <Title>Oeps!</Title>
        <TextLine>
          Je bent nog te jong om deze content te zien, probeer het over een paar jaar nog een keer.
        </TextLine>
        <div tw='flex justify-center mt-6'>
          <Link to='/' aria-label='terug naar de voorpagina'>
            <Button primary label='ga naar homepage' />
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const DateInputForm = () => {
  const [birthdate, setBirthdate] = useState({ day: '', month: '', year: '' });
  const [isFetching, setIsFetching] = useState(false);
  const [hasUpdatedBirthDate, setHasUpdatedBirthDate] = useState(false);
  const validBirthDay =
    isWrongDay(birthdate.day) ||
    isWrongMonth(birthdate.month) ||
    isWrongYear(birthdate.year) ||
    birthdate.month.length === 0 ||
    birthdate.year.length === 0 ||
    birthdate.day.length === 0 ||
    birthdate.year.length !== 4;

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setIsFetching(true);
      await userChangeBirthDate({
        new_birthdate: `${birthdate.month}-${birthdate.day}-${birthdate.year}`,
      });
      notifySuccess('Je geboortedatum is aangepast!');
      setIsFetching(false);
      setHasUpdatedBirthDate(true); // so button stays disabled
      setTimeout(() => {
        userUpdateAccessToken();
      }, 1000);
    } catch (err) {
      setIsFetching(false);
      if (err.response && err.response.data && err.response.data.error) {
        notifyError(err.response.data.error);
      } else {
        notifyError('Onbekende fout opgetreden');
      }
    }
  }

  return (
    <div tw='sm:aspect-w-16 sm:aspect-h-9 p-2 py-6 border-8 border-red-500'>
      <div tw='flex flex-col justify-center items-center'>
        <div tw='text-center mb-6'>
          <Title>Dit item is 18+.</Title>
          <TextLine>
            Voeg je geboortedatum toe aan je profiel om voortaan in één keer verder te gaan.
          </TextLine>
        </div>
        <div>
          <form onSubmit={handleSubmit}>
            <DateInput
              birthDate={birthdate}
              setBirthdate={setBirthdate}
              showDateFormatLabel={false}
            />
            <div tw='flex justify-end mt-4'>
              <Button
                type='submit'
                primary
                label='Bevestig'
                disabled={validBirthDay || isFetching || hasUpdatedBirthDate}
                loading={isFetching}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const isAgeInRange = (birthdate, minAge, maxAge = Infinity) => {
  // Convert the birthdate string to a Date object
  const birthDate = convertToDate(birthdate);

  // Get the current date
  const currentDate = new Date();

  // Calculate the age by comparing the birthdate to the current date
  let age = currentDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = currentDate.getMonth() - birthDate.getMonth();

  // If the current month is before the birth month, or if it's the birth month but the current day is before the birth day,
  // subtract one year from the age
  if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
    age--;
  }

  // Return true if the user is 18 or older, false otherwise
  return age >= minAge && age <= maxAge;
};

const getAge = (birthdate) => {
  const hasBirthdate = birthdate && birthdate !== undefined;
  return {
    isUnrealAge:
      hasBirthdate || !hasBirthdate ? (hasBirthdate ? isAgeInRange(birthdate, 99) : true) : false,
    is18Plus: hasBirthdate ? isAgeInRange(birthdate, 18, 99) : false,
    isBelow14: hasBirthdate ? isAgeInRange(birthdate, 0, 13) : false,
    isBetween15and17: hasBirthdate ? isAgeInRange(birthdate, 15, 17) : false,
  };
};

// promptedrun = age but formatted in 'yyyymmdd'
const useUserAgeConfirmation = (user) => {
  const [age, setAge] = useState(getAge(user?.promptedrun));

  useEffect(() => {
    setAge(getAge(user?.promptedrun));
  }, []);

  return age;
};

export const ItemDetails = ({ hideSuggestions = false, nextPlaylistItem, hideAds = false }) => {
  const { user } = useContext(UserContext);
  const {
    preference: {
      landscape,
      nsfwActive,
      detailsPageMobileBlock,
      stickyVideoMobile,
      commentsUnderVideo,
      sideBarLeftSide,
      listType: { detailsPage: detailsPageList },
    },
  } = useContext(PreferenceContext);

  const { currentItemId, item: currentItem, relatedItems } = useContext(ItemContext);

  const {
    imageSlider,
    slideIn: { active },
    setImageSlider,
    twitchLiveStream,
    windowSize,
  } = useContext(AppContext);

  useIncreaseItemViews(currentItem && currentItem.id);

  const isStickyVideoMobile =
    ['MOBILE'].includes(windowSize.adBreakPoint) &&
    stickyVideoMobile &&
    windowSize.width < windowSize.height * 0.75;
  const showCommentsUnderVideo =
    commentsUnderVideo && ['DESKTOP'].includes(windowSize.adBreakPoint);

  const hasImages =
    currentItem && currentItem.media
      ? currentItem.media.filter((i) => i.mediatype === 'FOTO').length >= 1
      : false;

  const { isUnrealAge, is18Plus, isBelow14, isBetween15and17 } = useUserAgeConfirmation(user);

  return (
    <>
      <MetaTags item={currentItem} />
      {currentItem && relatedItems && (
        <>
          {!user && currentItem.nsfw && (
            <RedirectPage itemId={currentItemId} currentItem={currentItem} />
          )}
          {user && currentItem.nsfw && !nsfwActive && <>Je bent ingelogd maar NSFW staat uit</>}
          {user && currentItem.nsfw && isBetween15and17 && <TooYoungBlock />}
          {((user && currentItem.nsfw && nsfwActive) || !currentItem.nsfw) && (
            <>
              <article>
                {currentItem.nsfw && nsfwActive && (isBelow14 || isUnrealAge) && <DateInputForm />}
                {((currentItem.nsfw && nsfwActive && is18Plus) || !currentItem.nsfw) && (
                  <ItemDetailsMedia
                    item={currentItem}
                    index={0}
                    videoEndedAction={!!nextPlaylistItem}
                    related={nextPlaylistItem || relatedItems}
                    counter={currentItem.media.length > 1}
                    openImageExternal={!hasImages}
                    isStickyVideoMobile={isStickyVideoMobile}
                    setSlidesActive={
                      hasImages
                        ? () =>
                            setImageSlider({
                              ...imageSlider,
                              active: true,
                              selected: 0,
                              images: currentItem.media.filter((i) => i.mediatype === 'FOTO'),
                            })
                        : null
                    }
                  >
                    {isStickyVideoMobile && <ItemActionsBar item={currentItem} isMobile />}
                  </ItemDetailsMedia>
                )}
                {!isStickyVideoMobile && (
                  <ItemActionsBar
                    item={currentItem}
                    isMobile={['MOBILE'].includes(windowSize.adBreakPoint)}
                    tw='z-20'
                  />
                )}

                <Container tw='py-4 lg:px-0'>
                  <ItemSummary item={currentItem} isDetailPage />
                  {currentItem.tags && (
                    <div tw='relative'>
                      <div tw='absolute h-full z-10 right-0 w-20 from-primary to-transparent bg-gradient-to-l right-0 lg:hidden pointer-events-none'></div>
                      <div
                        className='no-scrollbar'
                        tw='pt-4 pr-8 whitespace-nowrap overflow-x-auto lg:flex lg:flex-wrap'
                      >
                        <ItemTags tags={currentItem.tags} user={user} />
                      </div>
                    </div>
                  )}
                </Container>
                {currentItem.media.map((x, index) => (
                  <React.Fragment key={index}>
                    {index > 0 &&
                      ((is18Plus && currentItem.nsfw && nsfwActive) || !currentItem.nsfw) && (
                        <ItemDetailsMedia
                          related={relatedItems}
                          item={currentItem}
                          counter={
                            currentItem.media.filter((i) => i.mediatype === 'FOTO').length > 1
                          }
                          index={index}
                          openImageExternal={!hasImages}
                          setSlidesActive={
                            hasImages
                              ? () =>
                                  setImageSlider({
                                    ...imageSlider,
                                    active: true,
                                    selected: index,
                                    images: currentItem.media.filter((i) => i.mediatype === 'FOTO'),
                                  })
                              : null
                          }
                          tw='mb-4 last:mb-0'
                        />
                      )}
                  </React.Fragment>
                ))}
                {['MOBILE'].includes(windowSize.adBreakPoint) && !hideAds && (
                  <div tw='pb-4'>
                    <Ads Type={SideBarAds} adType='mobilelargerectangle' adNumber='t1' />
                  </div>
                )}
                {['TABLET'].includes(windowSize.adBreakPoint) && !hideAds && (
                  <div tw='px-4 pb-4 pt-0'>
                    <Ads Type={LazyloadAds} adType='billboard' adNumber='b1' />
                  </div>
                )}
                {!nextPlaylistItem &&
                  !twitchLiveStream &&
                  !['DESKTOP'].includes(windowSize.adBreakPoint) && <ItemMobileSelector />}
                {!hideSuggestions && !twitchLiveStream && (
                  <div
                    css={[
                      tw`mx-4 lg:mx-0`,
                      !active && tw`lg:mb-4`,
                      showCommentsUnderVideo && tw`grid grid-cols-5 mx-0 gap-6`,
                    ]}
                  >
                    {(detailsPageMobileBlock === 'items' ||
                      ['DESKTOP'].includes(windowSize.adBreakPoint)) && (
                      <div
                        css={[
                          sideBarLeftSide && tw`order-last`,
                          showCommentsUnderVideo && tw`col-span-3`,
                        ]}
                      >
                        <TitleRow
                          Type='h3'
                          sticky
                          tw='flex items-center -mx-4 px-4 lg:m-0 lg:p-0 z-20 top-[55px]'
                          RightBlock={RightBlock}
                        >
                          {detailsPageList &&
                          selectedListOptions.find((option) => option.value === detailsPageList)
                            ? 'Zie ook'
                            : 'Selecteer een lijst'}
                        </TitleRow>

                        <Suspense
                          fallback={
                            <ItemsGrid hideLoadMore>
                              <Loading itemPlaceholder whatIsLoading='dump' maxNumItems={12} />
                            </ItemsGrid>
                          }
                        >
                          <ItemsList
                            hideLoadMore
                            hideAds
                            listType={detailsPageList}
                            maxNumItems={12}
                            landscape={landscape}
                            itemId={currentItemId}
                          />
                        </Suspense>
                      </div>
                    )}

                    {((detailsPageMobileBlock === 'comments' &&
                      !['DESKTOP'].includes(windowSize.adBreakPoint)) ||
                      showCommentsUnderVideo) && (
                      <SideBarComments
                        itemId={currentItemId}
                        notSticky={showCommentsUnderVideo}
                        css={[showCommentsUnderVideo && tw`col-span-2 overflow-auto`]}
                        style={{ contain: showCommentsUnderVideo ? 'size' : '' }}
                        hideAds={!hideAds}
                      />
                    )}
                  </div>
                )}
                {['DESKTOP'].includes(windowSize.adBreakPoint) && !hideAds && (
                  <Ads
                    Type={LazyloadAds}
                    adType='largerectangle'
                    adNumber='b2'
                    css={[tw`mt-4`, !active && tw`lg:mb-4`]}
                  />
                )}
              </article>
            </>
          )}
        </>
      )}
    </>
  );
};
